import * as dateFnsCompat from 'utils/date-fns-compat';

import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { FiCheck } from 'react-icons/fi';
import connect from 'react-redux/lib/connect/connect';
import Spinner from 'react-spinkit';
import {
  lifecycle,
  withHandlers,
  withProps,
  withStateHandlers
} from 'recompose';
import { compose } from 'redux';
import months, { abbreviatedMonths } from '../../../../../constants/months';
import { TaskStatus } from '../../../../../constants/tasks';
import { defaultIndustryCpdCategorySorting } from '../../../../../services';
import {
  currentUserIndustry,
  currentUserSelector
} from '../../../../../state/modules/user';
import { noCapMap } from '../../../../../utils';
import {
  ControlGroup,
  ControlOption,
  FormField,
  GeneralTable,
  NumberFormField
} from '../../../../components';
import ValidationIcon from './validation-icon';
import { filterNonLegacy } from '../../../../../common/cpd-category-filter';

const getCpdDate = (month, day) => {
  const cpdDate = new Date();
  if (
    cpdDate.getMonth() > month - 1 ||
    (cpdDate.getMonth() === month - 1 && cpdDate.getDate() > day)
  ) {
    cpdDate.setFullYear(cpdDate.getFullYear() + 1, month - 1, day);
  } else {
    cpdDate.setFullYear(cpdDate.getFullYear(), month - 1, day);
  }
  return cpdDate;
};

const isEditingRows = (rows) =>
  fp.flow(
    fp.map(({ inEditMode }) => inEditMode),
    fp.contains(true)
  )(rows);

const EditableTrainingPlans = ({
  columns,
  rows,
  headerRows,
  applyToAll,
  isEditing,
  hasMandatoryCategory
}) => (
  <div className='editable-training-plans'>
    {rows.length > 0 ? (
      <GeneralTable
        rows={rows}
        headerRows={headerRows}
        columns={columns}
        className='table--editable-training-plan'
      />
    ) : (
      <Col xs={12}>
        <Row className='no-gutter striped'>
          <Col>
            <p>No training plans available.</p>
          </Col>
        </Row>
      </Col>
    )}
    {rows.length > 0 && hasMandatoryCategory ? (
      <p className='message'>* indicates a mandatory category</p>
    ) : null}
    {applyToAll ? (
      isEditing ? null : applyToAll.isUpdating ? (
        <Button className='bottom-gap pull-right'>
          <Spinner name='circle' className='spinner' fadeIn='none' />
        </Button>
      ) : (
        <Button className='bottom-gap pull-right' onClick={applyToAll.handler}>
          {applyToAll.text}
        </Button>
      )
    ) : null}
  </div>
);

const mapStateToProps = (state, { taskStack }) => ({
  industryCpdCategory: state.industry.cpdCategorys,
  currentUser: currentUserSelector(state),
  currentUserIndustry: fp.first(currentUserIndustry(state)),
  progressingTasks: fp.filter(({ status }) => status === TaskStatus.updating)(
    taskStack
  ),
  initiatedTasks: fp.filter(({ status }) => status === TaskStatus.initiated)(
    taskStack
  ),
  completedTasks: fp.filter(({ status }) => status === TaskStatus.completed)(
    taskStack
  ),
  isUpdatingEntities: fp.any(({ status }) => status !== TaskStatus.completed)(
    taskStack
  )
});
const mapDispatchToProps = (dispatch) => ({});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(({ industryCpdCategory }) => ({
    hasMandatoryCategory: fp.find(({ mandatory }) => mandatory)(
      industryCpdCategory
    )
  })),
  withStateHandlers(
    ({ entityTrainingPlans }) => ({
      isMatrixUpdating: false,
      mappedEntityTrainingPlans: fp.flow(
        fp.map((entityTrainingPlan) => ({
          ...entityTrainingPlan,
          inEditMode: false,
          cpdPoints: entityTrainingPlan.industry.cpdPoints,
          cpdDate: getCpdDate(
            entityTrainingPlan.industry.cpdMonth,
            entityTrainingPlan.industry.cpdDay
          ),
          validation: {}
        }))
      )(entityTrainingPlans)
    }),
    {
      updateMappedTrainingPlans: () => (mappedEntityTrainingPlans) => ({
        mappedEntityTrainingPlans
      }),
      setMatrixUpdating: () => () => ({
        isMatrixUpdating: true
      }),
      onDataRefetched:
        (state, { entityTrainingPlans }) =>
        () => ({
          isMatrixUpdating: false,
          mappedEntityTrainingPlans: fp.map((entityTrainingPlan) => ({
            ...entityTrainingPlan,
            inEditMode: false,
            cpdPoints: entityTrainingPlan.industry.cpdPoints,
            cpdDate: getCpdDate(
              entityTrainingPlan.industry.cpdMonth,
              entityTrainingPlan.industry.cpdDay
            ),
            validation: {}
          }))(entityTrainingPlans)
        }),
      onEditModeChange:
        ({ mappedEntityTrainingPlans }, { entityTrainingPlans }) =>
        (entityId) => ({
          mappedEntityTrainingPlans: fp.map((entityTrainingPlan) => ({
            ...entityTrainingPlan,
            cpdCategorys:
              entityId === entityTrainingPlan.entity.id
                ? fp.flow(
                    fp.find(
                      ({ entity }) => entity.id === entityTrainingPlan.entity.id
                    ),
                    fp.get('cpdCategorys')
                  )(entityTrainingPlans)
                : entityTrainingPlan.cpdCategorys,
            inEditMode:
              entityId === entityTrainingPlan.entity.id
                ? !entityTrainingPlan.inEditMode
                : entityTrainingPlan.inEditMode
          }))(mappedEntityTrainingPlans)
        }),
      onTrainingUpdateComplete:
        ({ mappedEntityTrainingPlans }) =>
        (entityId, { industryRole, trainingPlan }) => ({
          mappedEntityTrainingPlans: fp.map((entityTrainingPlan) =>
            entityId === entityTrainingPlan.entity.id
              ? {
                  ...entityTrainingPlan,
                  inEditMode: false,
                  industry: {
                    ...entityTrainingPlan.industry,
                    cpdPoints: industryRole.cpdPoints,
                    cpdMonth: industryRole.cpdMonth,
                    cpdDay: industryRole.cpdDay
                  },
                  cpdCategorys: trainingPlan
                }
              : entityTrainingPlan
          )(mappedEntityTrainingPlans)
        }),
      onCpdPointsChange:
        ({ mappedEntityTrainingPlans }) =>
        (entityId, points) => ({
          mappedEntityTrainingPlans: fp.map((entityTrainingPlan) => ({
            ...entityTrainingPlan,
            cpdPoints:
              entityId === entityTrainingPlan.entity.id
                ? points
                : entityTrainingPlan.cpdPoints
          }))(mappedEntityTrainingPlans)
        }),
      onCpdDateChange:
        ({ mappedEntityTrainingPlans }) =>
        (entityId, date) => ({
          mappedEntityTrainingPlans: fp.map((entityTrainingPlan) => ({
            ...entityTrainingPlan,
            cpdDate:
              entityId === entityTrainingPlan.entity.id
                ? date
                : entityTrainingPlan.cpdDate
          }))(mappedEntityTrainingPlans)
        }),
      toggleEntityCpdCategory:
        ({ mappedEntityTrainingPlans }) =>
        (entityId, selectedCpdCategoryId) => ({
          mappedEntityTrainingPlans: fp.map((entityTrainingPlan) => {
            if (entityTrainingPlan.entity.id === entityId) {
              const aoaSelected = fp.find(
                ({ cpdCategoryId }) => cpdCategoryId === selectedCpdCategoryId
              )(entityTrainingPlan.cpdCategorys);

              const updatedCpdCategorys = aoaSelected
                ? fp.filter(
                    ({ cpdCategoryId }) =>
                      cpdCategoryId !== selectedCpdCategoryId
                  )(entityTrainingPlan.cpdCategorys)
                : entityTrainingPlan.cpdCategorys.concat([
                    {
                      entityId: entityTrainingPlan.entity.id,
                      cpdCategoryId: selectedCpdCategoryId,
                      totalCpdPoints: 0
                    }
                  ]);
              return {
                ...entityTrainingPlan,
                cpdCategorys: updatedCpdCategorys
              };
            } else {
              return entityTrainingPlan;
            }
          })(mappedEntityTrainingPlans)
        })
    }
  ),
  lifecycle({
    componentWillReceiveProps(nextProps) {
      const {
        progressingTasks,
        initiatedTasks,
        completedTasks,
        initTaskStack,
        mappedEntityTrainingPlans,
        entityTrainingPlans,
        getData,
        onDataRefetched,
        onTrainingUpdateComplete
      } = nextProps;

      if (progressingTasks.length === 0 && initiatedTasks.length > 0) {
        fp.first(initiatedTasks).task();
      } else {
        fp.forEach(({ id, data }) => {
          if (
            fp.find(({ entity, inEditMode }) => inEditMode && entity.id === id)(
              mappedEntityTrainingPlans
            )
          ) {
            onTrainingUpdateComplete(id, data);
          }
        })(completedTasks);

        if (initiatedTasks.length === 0 && progressingTasks.length === 0) {
          if (completedTasks.length > 0) {
            initTaskStack([], false);
            getData();
          }
          if (
            JSON.stringify(entityTrainingPlans) !==
            JSON.stringify(this.props.entityTrainingPlans)
          ) {
            onDataRefetched();
          }
        }
      }
    }
  }),
  withProps(({ currentUserIndustry }) => ({
    validate: ({ cpdPoints }) => ({
      cpdPoints:
        currentUserIndustry.minimumCpdPoints <= cpdPoints
          ? null
          : `Must complete a minimum of ${currentUserIndustry.minimumCpdPoints} CPD hours to be compliant with FASEA’s legislative CPD instruction.`
    }),
    isValid: (validations) =>
      fp.flow(
        fp.map(({ validation }) => validation),
        fp.map(({ cpdPoints }) => !cpdPoints),
        fp.all(fp.identity)
      )(validations)
  })),
  withHandlers({
    applyTrainingPlanUpdates:
      ({
        mappedEntityTrainingPlans,
        saveCpdCategorys,
        initTaskStack,
        validate,
        isValid,
        updateMappedTrainingPlans,
        setMatrixUpdating,
        currentUserIndustry,
        mapEntityToData
      }) =>
      () => {
        const validatedTrainingPlans = fp.map((trainingPlan) => ({
          ...trainingPlan,
          validation: validate(trainingPlan)
        }))(mappedEntityTrainingPlans);

        if (isValid(validatedTrainingPlans)) {
          const plansToUpdate = fp.flow(
            fp.filter(({ inEditMode }) => inEditMode)
          )(mappedEntityTrainingPlans);

          const tasks = fp.map(
            ({ cpdPoints, cpdDate, industry, cpdCategorys, entity }) => ({
              task: () =>
                saveCpdCategorys({
                  updatedIndustry: {
                    ...industry,
                    cpdPoints,
                    cpdMonth: cpdDate.getMonth() + 1,
                    cpdDay: cpdDate.getDate()
                  },
                  industry,
                  cpdCategorys: fp.uniqBy(({ cpdCategoryId }) => cpdCategoryId)(
                    cpdCategorys
                  ),
                  entity
                }),
              id: entity.id,
              status: TaskStatus.initiated
            })
          )(plansToUpdate);
          setMatrixUpdating();
          initTaskStack(tasks, true);
        } else {
          updateMappedTrainingPlans(validatedTrainingPlans);
        }
      }
  }),
  withProps(({ industryCpdCategory }) => ({
    sortedIndustryCpdCategory: fp.flow(defaultIndustryCpdCategorySorting)(
      industryCpdCategory
    )
  })),
  withProps(
    ({
      entityName,
      industryCpdCategory,
      isUpdatingEntities,
      isMatrixUpdating,
      mappedEntityTrainingPlans,
      sortedIndustryCpdCategory,
      onEditModeChange,
      toggleEntityCpdCategory,
      onCpdPointsChange,
      onCpdDateChange,
      applyTrainingPlanUpdates
    }) => ({
      isEditing: isEditingRows(mappedEntityTrainingPlans),
      columns: [
        {
          key: 'edit',
          name: isEditingRows(mappedEntityTrainingPlans) ? (
            isUpdatingEntities || isMatrixUpdating ? (
              <Button id='apply-change-button'>
                <Spinner name='circle' className='spinner' fadeIn='none' />
              </Button>
            ) : (
              <Button
                id='apply-change-button'
                onClick={applyTrainingPlanUpdates}
              >
                Apply
              </Button>
            )
          ) : (
            'edit'
          ),
          className: 'edit'
        },
        {
          key: 'entity',
          name: entityName
        },
        {
          key: 'date',
          name: 'CPD year end date'
        },
        {
          key: 'total',
          name: 'total'
        }
      ].concat(
        fp.flow(
          filterNonLegacy,
          fp.map(({ id, name, parentCpdCategoryId }) => ({
            key: name,
            name: he.decode(name),
            className: parentCpdCategoryId
              ? 'childCpdCategory rotated'
              : 'rotated'
          }))
        )(sortedIndustryCpdCategory)
      ),
      headerRows: [
        [
          {
            value: '',
            width: 1,
            className: 'edit'
          },
          {
            value: '',
            width: 1,
            className: 'entity'
          },
          {
            value: '',
            width: 1,
            className: 'date'
          },
          {
            value: '',
            width: 1,
            className: 'total'
          }
        ].concat(
          fp.flow(
            filterNonLegacy,
            fp.reduce((result, { id, name, parentCpdCategoryId }) => {
              const parent = fp.find(({ id }) => id === parentCpdCategoryId)(
                sortedIndustryCpdCategory
              );
              const key = parent ? `${parent.name} child` : `${name}parentkey`;

              const isCpdCategoryPresent = fp.flow(
                fp.map(({ key }) => key),
                fp.contains(key)
              )(result);

              if (isCpdCategoryPresent) {
                return fp.map((span) => ({
                  ...span,
                  width: span.key === key ? span.width + 1 : span.width
                }))(result);
              } else {
                return result.concat([
                  {
                    key,
                    name: parent
                      ? `${parent.name} (at least one must be selected)`
                      : '',
                    width: 1
                  }
                ]);
              }
            }, []),
            fp.map(({ key, name, width }) => ({
              value: name,
              width,
              className: fp.lowerCase(key)
            }))
          )(sortedIndustryCpdCategory)
        )
      ],
      rows: fp.map(
        ({
          entity,
          cpdCategorys,
          industry,
          inEditMode,
          cpdPoints,
          cpdDate,
          validation
        }) =>
          Object.assign(
            ...[
              {
                edit: {
                  value: (
                    <ControlGroup
                      id='edit'
                      control='checkbox'
                      type='styled'
                      label={null}
                    >
                      <ControlOption
                        value={inEditMode}
                        checked={inEditMode}
                        onChange={(e) => onEditModeChange(entity.id)}
                      />
                    </ControlGroup>
                  ),
                  className: 'edit'
                }
              },
              {
                entity: {
                  value: he.decode(entity.name),
                  className: 'entity'
                }
              },
              {
                date: {
                  value: inEditMode ? (
                    <Row>
                      <Col className='no-padding' xs={5}>
                        <FormField id='cpd-day' type='select-item' label='Day'>
                          <select
                            id='cpd-day'
                            value={cpdDate.getDate()}
                            onChange={(e) =>
                              onCpdDateChange(
                                entity.id,
                                getCpdDate(
                                  cpdDate.getMonth() + 1,
                                  parseInt(e.target.value, 10)
                                )
                              )
                            }
                          >
                            {fp.flow(
                              fp.spread(fp.range),
                              fp.map((day) => (
                                <option key={day} value={day}>
                                  {day}
                                </option>
                              ))
                            )([1, dateFnsCompat.getDaysInMonth(cpdDate) + 1])}
                          </select>
                        </FormField>
                      </Col>
                      <Col className='no-padding' xs={1}></Col>
                      <Col className='no-padding' xs={5}>
                        <FormField
                          id='cpd-month'
                          type='select-item'
                          label='Month'
                        >
                          <select
                            id='cpd-month'
                            value={cpdDate.getMonth()}
                            onChange={(e) =>
                              onCpdDateChange(
                                entity.id,
                                getCpdDate(
                                  parseInt(e.target.value, 10) + 1,
                                  cpdDate.getDate()
                                )
                              )
                            }
                          >
                            {fp.flow(
                              fp.values,
                              noCapMap((month, index) => (
                                <option key={month} value={index}>
                                  {month}
                                </option>
                              ))
                            )(abbreviatedMonths)}
                          </select>
                        </FormField>
                      </Col>
                    </Row>
                  ) : (
                    `${industry.cpdDay} ${
                      fp.values(months)[industry.cpdMonth - 1]
                    }`
                  ),
                  className: 'date'
                }
              },
              {
                total: {
                  value: inEditMode ? (
                    <Row>
                      <Col className='no-padding' xs={9}>
                        <NumberFormField
                          id='total-cpd-points'
                          label={null}
                          value={cpdPoints}
                          onChange={(e) =>
                            onCpdPointsChange(entity.id, e.value)
                          }
                          required
                          min={0}
                        />
                      </Col>
                      <Col className='no-padding' xs={3}>
                        <ValidationIcon message={validation.cpdPoints} />
                      </Col>
                    </Row>
                  ) : (
                    <ControlGroup
                      id='total'
                      control='checkbox'
                      type='styled'
                      label={null}
                    >
                      <ControlOption
                        value={industry.cpdPoints}
                        checked={false}
                        label={<span>{industry.cpdPoints}</span>}
                        onChange={(e) => {}}
                      />
                    </ControlGroup>
                  ),
                  className: 'total'
                }
              }
            ].concat(
              fp.map(({ id, name, parentCpdCategoryId, mandatory }) => {
                const selected = fp.flow(
                  fp.map(({ cpdCategoryId }) => cpdCategoryId),
                  fp.contains(id)
                )(cpdCategorys);
                const siblingCpdCategory = fp.filter(
                  ({ parentCpdCategoryId: parentId }) =>
                    parentId === parentCpdCategoryId
                )(industryCpdCategory);
                const selectedChildren = fp.filter(({ cpdCategoryId }) =>
                  fp.flow(
                    fp.map(({ id }) => id),
                    fp.includes(cpdCategoryId)
                  )(siblingCpdCategory)
                )(cpdCategorys);
                const isLastChild =
                  selectedChildren.length === 1 &&
                  fp.get('cpdCategoryId')(fp.head(selectedChildren)) === id;
                const assignedPoints = fp.flow(
                  fp.find(({ cpdCategoryId }) => cpdCategoryId === id),
                  (maybeCpdCategory) =>
                    maybeCpdCategory ? maybeCpdCategory.totalCpdPoints : '-'
                )(cpdCategorys);
                const toggleHandler =
                  inEditMode && !mandatory && !isLastChild
                    ? () => {
                        toggleEntityCpdCategory(entity.id, id);
                      }
                    : () => {};
                return {
                  [name]: {
                    value: (
                      <ControlGroup
                        id={name}
                        control='checkbox'
                        type='styled'
                        label={null}
                      >
                        <ControlOption
                          key={name}
                          value={name}
                          checked={selected}
                          label={
                            <div id='points-label'>
                              {assignedPoints > 0 ? (
                                <span>{assignedPoints}</span>
                              ) : (
                                <span id='tick'>
                                  <FiCheck />
                                </span>
                              )}
                              {mandatory ? <span id='mandatory'>*</span> : null}
                            </div>
                          }
                          onChange={toggleHandler}
                        />
                      </ControlGroup>
                    ),
                    className: parentCpdCategoryId ? 'childCpdCategory' : ''
                  }
                };
              })(sortedIndustryCpdCategory)
            )
          )
      )(mappedEntityTrainingPlans)
    })
  )
)(EditableTrainingPlans);



// WEBPACK FOOTER //
// ./src/containers/pages/app/account-settings/partials/editable-training-plans.js